import { api } from './authService';
import { API_BASE_URL } from '@config/api';

export interface Recommendation {
  id: string;
  animal_id: string;
  user_id?: string;
  clinic_id?: string;
  type: 'vaccin' | 'deparazitare' | 'control' | 'nutritie' | 'produs' | 'comportament' | 'general';
  title: string;
  description: string;
  severity: 'info' | 'important' | 'urgent';
  due_at?: string;
  resolved_at?: string;
  resolved_by?: string;
  source_type: string;
  source_rule?: string;
  source_tip_id?: string;
  metadata?: any;
  last_check?: string;
  created_at: string;
  updated_at: string;
}

export interface RecommendationFilters {
  animalId?: string;
  type?: string;
  severity?: string;
  resolved?: boolean;
  page?: number;
  limit?: number;
}

export interface RecommendationStatistics {
  total: number;
  urgent: number;
  important: number;
  info: number;
  resolved: number;
  byType: Record<string, number>;
}

class RecommendationsService {
  /**
   * Obține recomandările pentru utilizatorul autentificat
   */
  async getRecommendations(filters: RecommendationFilters = {}): Promise<{ data: Recommendation[]; total: number; pagination: any }> {
    try {
      const response = await api.get(`${API_BASE_URL}/recommendations`, {
        params: filters
      });

      if (response.data.success) {
        return {
          data: response.data.data || [],
          total: response.data.pagination?.total || 0,
          pagination: response.data.pagination
        };
      }

      return { data: [], total: 0, pagination: {} };
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      throw error;
    }
  }

  /**
   * Obține o recomandare specifică
   */
  async getRecommendationById(id: string): Promise<Recommendation | null> {
    try {
      const response = await api.get(`${API_BASE_URL}/recommendations/${id}`);

      if (response.data.success) {
        return response.data.data;
      }

      return null;
    } catch (error) {
      console.error('Error fetching recommendation:', error);
      throw error;
    }
  }

  /**
   * Marchează o recomandare ca rezolvată
   */
  async resolveRecommendation(id: string): Promise<Recommendation> {
    try {
      const response = await api.post(`${API_BASE_URL}/recommendations/${id}/resolve`);

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to resolve recommendation');
    } catch (error: any) {
      console.error('Error resolving recommendation:', error);
      throw new Error(error.response?.data?.error || 'Failed to resolve recommendation');
    }
  }

  /**
   * Recalculează recomandările
   */
  async recalculateRecommendations(animalId?: string): Promise<{ created: number; updated: number }> {
    try {
      const response = await api.post(`${API_BASE_URL}/recommendations/recalculate`, {
        animalId,
        force: true
      });

      if (response.data.success) {
        return response.data.data;
      }

      throw new Error(response.data.error || 'Failed to recalculate recommendations');
    } catch (error: any) {
      console.error('Error recalculating recommendations:', error);
      throw new Error(error.response?.data?.error || 'Failed to recalculate recommendations');
    }
  }

  /**
   * Obține statistici despre recomandări
   */
  async getStatistics(animalId?: string): Promise<RecommendationStatistics> {
    try {
      const response = await api.get(`${API_BASE_URL}/recommendations/statistics/overview`, {
        params: { animalId }
      });

      if (response.data.success) {
        return response.data.data;
      }

      return {
        total: 0,
        urgent: 0,
        important: 0,
        info: 0,
        resolved: 0,
        byType: {}
      };
    } catch (error) {
      console.error('Error fetching statistics:', error);
      throw error;
    }
  }
}

export default new RecommendationsService();

